import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from 'react';

import {
  AgUiEvent,
  ConnectionStatus,
  GatewayMessage,
  GatewayProgressState,
} from '../../types/gateway-chat-types';
import {
  EventHandlerContext,
  handleConnectionClosed,
  handleConnectionEstablished,
  handleError,
  handleMetadata,
  handleProgressUpdate,
  handleRunFinished,
  handleRunStarted,
  handleSessionHistory,
  handleTextMessageContent,
  handleTextMessageEnd,
  handleTextMessageStart,
} from '../../utils/event-handlers';

const MAX_RECENT_EVENTS = 100;

const createInitialProgressState = (): GatewayProgressState => ({
  percentage: 0,
  message: '',
  active: false,
});

export interface ChatStreamState {
  messages: GatewayMessage[];
  recentEvents: AgUiEvent[];
  progressState: GatewayProgressState;
  connectionStatus: ConnectionStatus;
  currentRunId: string;
}

export interface UseChatStreamStateResult extends ChatStreamState {
  setMessages: Dispatch<SetStateAction<GatewayMessage[]>>;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setCurrentRunId: (runId: string) => void;
  resetConversationState: () => void;
  handleAgUiEvent: (event: AgUiEvent) => void;
}

export const useChatStreamState = (): UseChatStreamStateResult => {
  const [messages, setMessages] = useState<GatewayMessage[]>([]);
  const [recentEvents, setRecentEvents] = useState<AgUiEvent[]>([]);
  const [progressState, setProgressState] = useState<GatewayProgressState>(
    createInitialProgressState
  );
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [currentRunId, setCurrentRunIdState] = useState('');

  const currentRunIdRef = useRef('');

  const setCurrentRunId = useCallback((runId: string) => {
    currentRunIdRef.current = runId;
    setCurrentRunIdState(runId);
  }, []);

  const resetConversationState = useCallback(() => {
    setMessages([]);
    setRecentEvents([]);
    setProgressState(createInitialProgressState());
    setCurrentRunId('');
  }, [setCurrentRunId]);

  // Create handler context with all required state setters
  const handlerContext = useMemo<EventHandlerContext>(
    () => ({
      setMessages,
      setConnectionStatus,
      setProgressState,
      setCurrentRunId,
      currentRunIdRef,
      createInitialProgressState,
    }),
    [setCurrentRunId]
  );

  const handleAgUiEvent = useCallback(
    (event: AgUiEvent) => {
      // Track all events except high-frequency ones
      if (event.type !== 'PROGRESS_UPDATE' && event.type !== 'TEXT_MESSAGE_CONTENT') {
        setRecentEvents((prev) => [event, ...prev].slice(0, MAX_RECENT_EVENTS));
      }

      const runIdFromEvent = event.runId ?? undefined;
      const effectiveRunId = runIdFromEvent ?? currentRunIdRef.current;

      // Delegate to individual event handlers
      switch (event.type) {
        case 'CONNECTION_ESTABLISHED':
          handleConnectionEstablished(handlerContext);
          break;

        case 'CONNECTION_CLOSED':
          handleConnectionClosed(handlerContext);
          break;

        case 'SESSION_HISTORY':
          handleSessionHistory(event, handlerContext);
          break;

        case 'RUN_STARTED':
          handleRunStarted(event, handlerContext);
          break;

        case 'PROGRESS_UPDATE':
          handleProgressUpdate(event, handlerContext);
          break;

        case 'TEXT_MESSAGE_START':
          handleTextMessageStart(effectiveRunId, handlerContext);
          break;

        case 'TEXT_MESSAGE_CONTENT':
          handleTextMessageContent(event, effectiveRunId, handlerContext);
          break;

        case 'TEXT_MESSAGE_END':
          handleTextMessageEnd(effectiveRunId, handlerContext);
          break;

        case 'ERROR':
          handleError(event, effectiveRunId, handlerContext);
          break;

        case 'RUN_FINISHED':
          handleRunFinished(event, effectiveRunId, handlerContext);
          break;

        case 'METADATA':
          // Handle METADATA events that may contain domain-specific info (e.g., OMOP session)
          handleMetadata(event, handlerContext);
          break;

        default:
          break;
      }
    },
    [handlerContext]
  );

  return {
    messages,
    recentEvents,
    progressState,
    connectionStatus,
    currentRunId,
    setMessages,
    setConnectionStatus,
    setCurrentRunId,
    resetConversationState,
    handleAgUiEvent,
  };
};
